import * as React from 'react';
import { clsx } from 'clsx';
import {
  YMap,
  YMapControls,
  YMapDefaultFeaturesLayer,
  YMapDefaultSchemeLayer,
  YMapMarker,
  YMapProvider,
  YMapZoomControl,
} from '@cian/frontend-ymaps-components';
import type { YMaps } from '@cian/frontend-ymaps-components/es/types/ymaps';

import { INewbuildingMapPin } from '../../types/map';
import { IPinCluster, clusterPins, getMapLocation, toLngLat } from './utils';
import { MAP_MARGIN, TMapPlatform } from '../../constants/map';
import { MapCluster } from './parts/MapCluster';
import { MapErrorBoundary } from '../MapErrorBoundary';
import { MapPin, MobileMapPin } from './parts/MapPin';
import { MapZoomWatcher } from './parts/MapZoomWatcher';
import { useApplicationContext } from '../../utils/applicationContext';
import * as styles from './styles.css';

const CAMERA_ANIMATION_DURATION = 300;

export type TNewbuildingsMapPlatform = TMapPlatform;

export interface INewbuildingsMapProps {
  apiKey: string;
  pins: INewbuildingMapPin[];
  platform?: TNewbuildingsMapPlatform;
  onPinClick(pin: INewbuildingMapPin): void;
}

export function NewbuildingsMap({
  apiKey,
  pins,
  platform = 'desktop',
  onPinClick,
}: INewbuildingsMapProps): React.JSX.Element {
  const { logger } = useApplicationContext();
  // Зум нужен для кластеризации. Пока карта не сообщила своё положение, кластеров нет.
  const [zoom, setZoom] = React.useState<number | null>(null);
  const [location, setLocation] = React.useState<YMaps.YMapLocationRequest>(() =>
    getMapLocation(pins.map(pin => pin.coordinates)),
  );
  // ЖК, карточки которых уже открывали с карты: их пины показываются приглушёнными.
  const [seenIds, setSeenIds] = React.useState<ReadonlySet<number>>(() => new Set());
  const mapRef = React.useRef<YMaps.YMap | null>(null);

  const clusters = React.useMemo(() => clusterPins(pins, zoom, platform), [pins, zoom, platform]);

  // Кластеры зависят только от зума: расстояние между точками в пикселях от сдвига карты не меняется.
  const handleZoomChange = React.useCallback((nextExactZoom: number) => {
    const nextZoom = Math.round(nextExactZoom);

    setZoom(current => (current === nextZoom ? current : nextZoom));
  }, []);

  const handlePinClick = React.useCallback(
    (pin: INewbuildingMapPin) => {
      const { id } = pin.newbuilding;

      setSeenIds(current => (current.has(id) ? current : new Set(current).add(id)));
      onPinClick(pin);
    },
    [onPinClick],
  );

  const handleClusterClick = React.useCallback((cluster: IPinCluster) => {
    setLocation({
      ...getMapLocation(cluster.pins.map(pin => pin.coordinates)),
      duration: CAMERA_ANIMATION_DURATION,
    });
  }, []);

  const handleMapError = React.useCallback(
    (error: unknown) => {
      const normalizedError: Error = error instanceof Error ? error : new Error(String(error));

      logger.error(normalizedError, { degradation: 'Карта ЖК в сравнении' });
    },
    [logger],
  );

  const containerPlatform = `container--${platform}`;

  return (
    <div className={clsx(styles['container'], styles[containerPlatform])}>
      <MapErrorBoundary onError={handleMapError}>
        <YMapProvider apiKey={apiKey} lang="ru_RU" onError={handleMapError}>
          <YMap ref={mapRef} location={location} margin={MAP_MARGIN[platform]} zoomRounding="smooth">
            <YMapDefaultSchemeLayer />
            <YMapDefaultFeaturesLayer />
            <MapZoomWatcher mapRef={mapRef} onZoomChange={handleZoomChange} />
            {clusters.map(cluster => {
              const [pin] = cluster.pins;

              if (cluster.pins.length > 1) {
                return (
                  <YMapMarker blockEvents key={cluster.id} coordinates={toLngLat(cluster.coordinates)}>
                    <MapCluster
                      count={cluster.pins.length}
                      isSeen={cluster.pins.every(clusteredPin => seenIds.has(clusteredPin.newbuilding.id))}
                      onClick={() => handleClusterClick(cluster)}
                    />
                  </YMapMarker>
                );
              }

              const Pin = platform === 'mobile' ? MobileMapPin : MapPin;

              return (
                <YMapMarker blockEvents key={cluster.id} coordinates={toLngLat(pin.coordinates)}>
                  <Pin isSeen={seenIds.has(pin.newbuilding.id)} pin={pin} onClick={handlePinClick} />
                </YMapMarker>
              );
            })}
            <YMapControls position="right">
              <YMapZoomControl />
            </YMapControls>
          </YMap>
        </YMapProvider>
      </MapErrorBoundary>
    </div>
  );
}
